const CONSENT_ANALYTICS_GRANTED = {
  analytics_storage: 'granted',
} as const;

const CONSENT_ANALYTICS_DENIED = {
  analytics_storage: 'denied',
} as const;

const CONSENT_ADS_GRANTED = {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
} as const;

const CONSENT_ADS_DENIED = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
} as const;

const TRACK_PAGE_VIEW_RETRY_INTERVAL_MS = 100;
const TRACK_PAGE_VIEW_MAX_RETRIES = 20;

class GTMService {
  private readonly gtmId: string;

  private hasMeasurementConsent: boolean | null = null;

  constructor() {
    this.gtmId = import.meta.env.VITE_PUBLIC_GTM_ID || '';
  }

  get isGtmAvailable(): boolean {
    return !!this.gtmId;
  }

  updateConsent(preferences: Record<string, boolean>): void {
    if (!this.isGtmAvailable) {
      return;
    }

    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      return;
    }

    try {
      const hasMeasurementConsent = preferences.measurement === true;
      this.hasMeasurementConsent = hasMeasurementConsent;

      const hasAdsConsent =
        preferences.marketing === true ||
        preferences.ad_personalization === true ||
        preferences.ads === true;

      const analyticsState = hasMeasurementConsent
        ? CONSENT_ANALYTICS_GRANTED
        : CONSENT_ANALYTICS_DENIED;

      const adsState = hasAdsConsent ? CONSENT_ADS_GRANTED : CONSENT_ADS_DENIED;

      const consentState = { ...analyticsState, ...adsState };

      window.gtag('consent', 'update', consentState);
    } catch (error) {
      console.error('GTM consent handling failed:', error);
    }
  }

  trackPageView(pathname: string, hasMeasurementConsent?: boolean): void {
    if (!this.isGtmAvailable) {
      return;
    }

    const hasConsent = hasMeasurementConsent ?? this.hasMeasurementConsent;
    if (hasConsent !== true) {
      return;
    }

    this.trackPageViewWhenReady(pathname, 0);
  }

  private trackPageViewWhenReady(pathname: string, retryCount: number): void {
    if (!window.dataLayer) {
      if (retryCount < TRACK_PAGE_VIEW_MAX_RETRIES) {
        setTimeout(
          () => this.trackPageViewWhenReady(pathname, retryCount + 1),
          TRACK_PAGE_VIEW_RETRY_INTERVAL_MS
        );
      }
      return;
    }

    try {
      window.dataLayer.push({
        event: 'page_view',
        page_path: pathname,
      });
    } catch (error) {
      console.error('GTM page view tracking failed:', error);
    }
  }
}

export const gtmService = new GTMService();
